import React, { useEffect, useState, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { PDFViewer, pdf } from '@react-pdf/renderer';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faSpinner, faExclamationTriangle, faFileInvoice, faDownload } from '@fortawesome/free-solid-svg-icons';
import PdfTemplates from '../../components/PdfTemplates';
import { getBas264Image } from '../../controllers/V1/generalDataController';
import { voucherDetails } from '../../controllers/V1/winnersControllers';
import { useParams } from 'react-router-dom';
import moment from 'moment';


// Helper function to detect image format and format base64 data URI
const formatImageDataUri = (base64Image, imageUrl = '') => {
  if (!base64Image || typeof base64Image !== 'string') return base64Image;
  
  // If already formatted as data URI, return as is
  if (base64Image.startsWith('data:')) return base64Image;
  
  // If it's a URL, return as is
  if (base64Image.startsWith('http')) return base64Image;
  
  // Detect format from URL extension
  let imageType = 'jpeg'; // default
  if (imageUrl) {
    const urlLower = imageUrl.toLowerCase();
    if (urlLower.includes('.png')) {
      imageType = 'png';
    } else if (urlLower.includes('.jpg') || urlLower.includes('.jpeg')) {
      imageType = 'jpeg';
    }
  }
  
  // Try to detect from base64 data (magic bytes)
  // PNG: starts with iVBORw0KGgo
  // JPEG: starts with /9j/
  if (base64Image.startsWith('iVBORw0KGgo')) {
    imageType = 'png';
  } else if (base64Image.startsWith('/9j/')) {
    imageType = 'jpeg';
  }
  
  return `data:image/${imageType};base64,${base64Image}`;
};

// Helper function to process a single image field
const processImageField = async (imageData, fieldName) => {
  if (!imageData) return null;
  
  try {
    // Extract image URL - handle both string and object formats
    const imageUrl = typeof imageData === 'string' 
      ? imageData 
      : imageData[fieldName] || imageData;
    
    if (!imageUrl) return null;
    
    // Fetch base64 image from API
    const base64Image = await getBas264Image({image_url: imageUrl});
    
    // Check if API returned an error
    if (base64Image && typeof base64Image === 'object' && base64Image.status === false) {
      console.error(`Failed to fetch ${fieldName} image:`, base64Image.message);
      return null;
    }
    
    // Format base64 as data URI with detected image type
    if (base64Image && typeof base64Image === 'string') {
      const formattedImage = formatImageDataUri(base64Image, imageUrl);
      
      return {
        ...(typeof imageData === 'object' ? imageData : {}),
        [fieldName]: formattedImage,
      };
    }
    
    return null;
  } catch (error) {
    console.error(`Error processing ${fieldName} image:`, error);
    return null;
  }
};

// Export processFormDataImages for reuse
export const processFormDataImages = async (formData) => {
  if (!formData) return formData;
  
  const processedData = { ...formData };
  
  // Process all image fields using the helper function
  if (processedData.doc) {
    const processedDoc = await processImageField(processedData.doc, 'doc');
    if (processedDoc) {
      processedData.doc = processedDoc;
    }
  }
  
  if (processedData.order_slip) {
    const processedOrderSlip = await processImageField(processedData.order_slip, 'order_slip');
    if (processedOrderSlip) {
      processedData.order_slip = processedOrderSlip;
    }
  }
  
  if (processedData.redeem_slip) {
    const processedRedeemSlip = await processImageField(processedData.redeem_slip, 'redeem_slip');
    if (processedRedeemSlip) {
      processedData.redeem_slip = processedRedeemSlip;
    }
  }
  
  if (processedData.other_attachment) {
    const processedOtherAttachment = await processImageField(processedData.other_attachment, 'other_attachment');
    if (processedOtherAttachment) {
      processedData.other_attachment = processedOtherAttachment;
    }
  }

  if (processedData.signature) {
    const processedsignature = await processImageField(processedData.signature, 'signature');
    if (processedsignature) {
      processedData.signature = processedsignature;
    }
  }

  if (processedData.images) {
    const processedImages = await processImageField(processedData.images, 'images');
    if (processedImages) {
      processedData.images = processedImages;
    }
  }

  if (processedData.images2) {
    const processedImages2 = await processImageField(processedData.images2, 'images2');
    if (processedImages2) {
      processedData.images2 = processedImages2;
    }
  }
  
  return processedData;
};

// Helper function to detect mobile devices
const isMobileDevice = () => {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
    (window.innerWidth <= 768);
};

const WinningVoucher = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { order_id } = useParams();
  const [processedData, setProcessedData] = useState(null);
  const [voucherData, setVoucherData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [processingImages, setProcessingImages] = useState(false);
  const [pdfBlobUrl, setPdfBlobUrl] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const pdfBlobUrlRef = useRef(null);

  const fetchVoucherDetails = async () => {
    try {
      setLoading(true);
      setError(null);
      console.log("order_id", order_id);
      const options = {
        order_id: order_id,
      };
      const res = await voucherDetails(options);
      if (res.status === true) {
        setVoucherData(res.result);
        setLoading(false);
      } else {
        setError(res.message || 'Failed to fetch voucher details');
        setLoading(false);
      }
    } catch (error) {
      console.error('Error fetching voucher details:', error);
      setError(error.message || 'An unexpected error occurred while fetching voucher details');
      setLoading(false);
    }
  }


  useEffect(() => {
    setIsMobile(isMobileDevice());
  }, []);

  useEffect(() => {
    fetchVoucherDetails();
  }, [order_id]);
  
  useEffect(() => {
    if (voucherData) {
      setProcessingImages(true);
      processFormDataImages(voucherData).then((data) => {
        setProcessedData(data);
        setProcessingImages(false);
      }).catch((error) => {
        console.error('Error processing images:', error);
        setProcessingImages(false);
        // Still show the voucher even if image processing fails
      });
    }
  }, [voucherData]);

  // Generate PDF blob URL for mobile devices
  useEffect(() => {
    if (isMobile && processedData) {
      const generatePdfBlob = async () => {
        try {
          // Cleanup previous blob URL if exists
          if (pdfBlobUrlRef.current) {
            URL.revokeObjectURL(pdfBlobUrlRef.current);
            pdfBlobUrlRef.current = null;
          }

          const doc = <PdfTemplates voucherData={processedData || voucherData} />;
          const asPdf = pdf(doc);
          const blob = await asPdf.toBlob();
          const url = URL.createObjectURL(blob);
          pdfBlobUrlRef.current = url;
          setPdfBlobUrl(url);
        } catch (error) {
          console.error('Error generating PDF blob:', error);
        }
      };
      generatePdfBlob();
    }

    // Cleanup blob URL on unmount or when dependencies change
    return () => {
      if (pdfBlobUrlRef.current) {
        URL.revokeObjectURL(pdfBlobUrlRef.current);
        pdfBlobUrlRef.current = null;
      }
    };
  }, [isMobile, processedData, voucherData]);

  // Loading state
  if (loading) {
    return (
      <div style={{
        height: "90vh",
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#f5f5f5',
      }}>
        <div style={{
          textAlign: 'center',
          padding: '40px',
          backgroundColor: '#fff',
          borderRadius: '15px',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
          maxWidth: '500px',
          width: '90%',
        }}>
          <FontAwesomeIcon 
            icon={faSpinner} 
            spin 
            style={{
              fontSize: '48px',
              color: '#4CAF50',
              marginBottom: '20px',
            }}
          />
          <h2 style={{
            margin: '0 0 10px 0',
            color: '#333',
            fontSize: '24px',
            fontWeight: '600',
          }}>
            Loading Voucher
          </h2>
          <p style={{
            margin: '0',
            color: '#666',
            fontSize: '16px',
          }}>
            Please wait while we fetch your voucher details...
          </p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div style={{
        height: "90vh",
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#f5f5f5',
      }}>
        <div style={{
          textAlign: 'center',
          padding: '40px',
          backgroundColor: '#fff',
          borderRadius: '15px',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
          maxWidth: '500px',
          width: '90%',
        }}>
          <FontAwesomeIcon 
            icon={faExclamationTriangle} 
            style={{
              fontSize: '64px',
              color: '#f44336',
              marginBottom: '20px',
            }}
          />
          <h2 style={{
            margin: '0 0 10px 0',
            color: '#333',
            fontSize: '24px',
            fontWeight: '600',
          }}>
            Error Loading Voucher
          </h2>
          <p style={{
            margin: '0 0 30px 0',
            color: '#666',
            fontSize: '16px',
            lineHeight: '1.5',
          }}>
            {error}
          </p>
          <button
            onClick={() => {
              setError(null);
              fetchVoucherDetails();
            }}
            style={{
              padding: '12px 30px',
              backgroundColor: '#4CAF50',
              color: '#fff',
              border: 'none',
              borderRadius: '8px',
              fontSize: '16px',
              cursor: 'pointer',
              fontWeight: '500',
              transition: 'background-color 0.3s',
            }}
            onMouseOver={(e) => e.target.style.backgroundColor = '#45a049'}
            onMouseOut={(e) => e.target.style.backgroundColor = '#4CAF50'}
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // No voucher data found
  if (!voucherData || (voucherData && Object.keys(voucherData).length === 0)) {
    return (
      <div style={{
        height: "90vh",
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#f5f5f5',
      }}>
        <div style={{
          textAlign: 'center',
          padding: '40px',
          backgroundColor: '#fff',
          borderRadius: '15px',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
          maxWidth: '500px',
          width: '90%',
        }}>
          <FontAwesomeIcon 
            icon={faFileInvoice} 
            style={{
              fontSize: '64px',
              color: '#ff9800',
              marginBottom: '20px',
            }}
          />
          <h2 style={{
            margin: '0 0 10px 0',
            color: '#333',
            fontSize: '24px',
            fontWeight: '600',
          }}>
            No Voucher Found
          </h2>
          <p style={{
            margin: '0 0 30px 0',
            color: '#666',
            fontSize: '16px',
            lineHeight: '1.5',
          }}>
            We couldn't find any voucher data for this order. Please verify the order ID and try again.
          </p>
          <button
            onClick={() => {
              setError(null);
              fetchVoucherDetails();
            }}
            style={{
              padding: '12px 30px',
              backgroundColor: '#2196F3',
              color: '#fff',
              border: 'none',
              borderRadius: '8px',
              fontSize: '16px',
              cursor: 'pointer',
              fontWeight: '500',
              transition: 'background-color 0.3s',
            }}
            onMouseOver={(e) => e.target.style.backgroundColor = '#0b7dda'}
            onMouseOut={(e) => e.target.style.backgroundColor = '#2196F3'}
          >
            Refresh
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ height: "90vh", textAlign: 'center' }}>
        {processingImages && (
          <div style={{
            position: 'fixed',
            top: '20px',
            right: '20px',
            backgroundColor: '#fff',
            padding: '15px 25px',
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            zIndex: 1000,
          }}>
            <FontAwesomeIcon 
              icon={faSpinner} 
              spin 
              style={{
                fontSize: '18px',
                color: '#4CAF50',
              }}
            />
            <span style={{
              color: '#333',
              fontSize: '14px',
              fontWeight: '500',
            }}>
              Processing images...
            </span>
          </div>
        )}
        <div className="invoice-container" style={{
            width: '100%',
            height: '100vh',
            margin: 'auto',
            backgroundColor: '#fff',
            padding: '20px',
            border: '1px solid #ccc',
            borderRadius: '10px',
            boxShadow: '0 0 10px rgba(0, 0, 0, 0.1)',
            fontSize: '13px',
            display: 'flex',
            flexDirection: 'column',
        }}>
            {isMobile ? (
              // Mobile: Use iframe with blob URL
              <div style={{
                width: '100%',
                height: 'calc(100vh - 100px)',
                minHeight: '600px',
                display: 'flex',
                flexDirection: 'column',
                gap: '15px',
              }}>
                {pdfBlobUrl ? (
                  <>
                    <div style={{
                      display: 'flex',
                      justifyContent: 'center',
                      gap: '10px',
                    }}>
                      <a
                        href={pdfBlobUrl}
                        download={`Winning_Voucher_${processedData?.order_id || voucherData?.order_id || ''}_${moment(new Date()).format('DD-MM-YYYY')}.pdf`}
                        style={{
                          padding: '12px 24px',
                          backgroundColor: '#4CAF50',
                          color: '#fff',
                          textDecoration: 'none',
                          borderRadius: '8px',
                          fontSize: '16px',
                          fontWeight: '500',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '8px',
                        }}
                      >
                        <FontAwesomeIcon icon={faDownload} />
                        Download PDF
                      </a>
                    </div>
                    <iframe
                      src={pdfBlobUrl}
                      style={{
                        width: '100%',
                        height: 'calc(100vh - 180px)',
                        minHeight: '500px',
                        border: '1px solid #ddd',
                        borderRadius: '8px',
                      }}
                      title="PDF Viewer"
                    />
                  </>
                ) : (
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '100%',
                    flexDirection: 'column',
                    gap: '20px',
                  }}>
                    <FontAwesomeIcon 
                      icon={faSpinner} 
                      spin 
                      style={{
                        fontSize: '48px',
                        color: '#4CAF50',
                      }}
                    />
                    <p style={{
                      color: '#666',
                      fontSize: '16px',
                    }}>
                      Generating PDF...
                    </p>
                  </div>
                )}
              </div>
            ) : (
              // Desktop: Use PDFViewer
              <PDFViewer style={{
                width: '100%',
                height: 'calc(100vh - 100px)',
                minHeight: '600px',
              }}>
                <PdfTemplates voucherData={processedData || voucherData} />      
              </PDFViewer>
            )}
        </div>
    </div>
  );
};

export default WinningVoucher;
